import { PLATFORM_INSTITUTION_CODE, type SessionUser } from "@eduleo/shared";

/** Schools sign in here with Institution ID, User ID and password. */
export const LOGIN_PATH = "/login";

/** Eduleo's own team signs in here with User ID and password (then a code, when MFA is on). */
export const SYSTEM_ADMIN_LOGIN_PATH = "/system-admin/login";

/** Where the platform console lives. Signed-out visits here go to the team's sign-in page. */
export function isPlatformPath(path: string | null | undefined): boolean {
  if (!path) return false;
  const pathname = path.split(/[?#]/)[0] ?? "";
  return pathname === "/platform" || pathname.startsWith("/platform/");
}

/** Where a school's signatory signs Eduleo's agreement from an emailed link, without signing in. */
export const SIGNING_PATH = "/sign/:token";

/** The public signing page: no console session is restored or used there. */
export function isSigningPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return path.startsWith("/sign/");
}

/** Where an emailed "set your password" link opens. The secret is in the #fragment. */
export const SET_PASSWORD_PATH = "/set-password";

/** The public set-password page: like the signing page, no session is restored there. */
export function isSetPasswordPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return path === SET_PASSWORD_PATH || path.startsWith(`${SET_PASSWORD_PATH}/`);
}

/** True for either sign-in page, so nobody is sent "back" to one after signing in. */
export function isSignInPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return path.startsWith(LOGIN_PATH) || path.startsWith(SYSTEM_ADMIN_LOGIN_PATH);
}

/** The sign-in page this person uses. */
export function signInPathFor(user: Pick<SessionUser, "institution"> | null): string {
  return user?.institution.code === PLATFORM_INSTITUTION_CODE
    ? SYSTEM_ADMIN_LOGIN_PATH
    : LOGIN_PATH;
}

/**
 * Where a signed-in person goes: back to `from` when it is their side of the app (the console
 * for Eduleo's team, everything else for schools), otherwise to their own home.
 */
export function landingFor(user: Pick<SessionUser, "institution"> | null, from: string): string {
  const team = user?.institution.code === PLATFORM_INSTITUTION_CODE;
  if (team) return isPlatformPath(from) ? from : "/platform";
  return isPlatformPath(from) || isSignInPath(from) ? "/" : from;
}
